<template>
  <ValidationObserver ref="observer">
    <div v-if="loadMenu" :class="{ 'menu-page-scroll': !embedded }">
      <b-card class="preview-card shadow-sm">
        <div class="preview-charts">
          <!-- LEFT: Existing Chart -->
          <div class="chart-box">
            <h6 class="text-muted">Existing Chart</h6>
            <div :id="id" ref="r1c1" class="scrollmenu"></div>
          </div>

          <!-- RIGHT: Preview Chart -->
          <div class="chart-box">
            <h6 class="text-muted">Preview Chart</h6>
            <div v-if="previewLoading" class="text-center p-4">
              Loading preview...
            </div>
            <div v-else :id="id" ref="r1c2" class="scrollmenu"></div>
          </div>
        </div>
        <b-row class="preview-sql-row">
          <b-col md="6" class="preview-col-wrap">
            <b-form-group
              label="DataSQL"
              label-align="left"
              class="sql-form-group"
            >
              <b-form-textarea
                :value="cleanedDataSql"
                disabled
                auto-grow
                :no-resize="false"
                class="sql-textarea"
              />
            </b-form-group>
          </b-col>
          <b-col md="6" class="preview-col-wrap">
            <b-form-group
              label="DataSQL"
              label-align="left"
              class="sql-form-group"
            >
              <b-form-textarea
                v-model="dataSqlId"
                auto-grow
                :no-resize="false"
                class="sql-textarea"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <!-- Viz Params Mapping Tables -->
        <b-row
          class="preview-mapping-row pb-3"
          v-if="vizParams && Object.keys(vizParams).length"
        >
          <b-col md="6" class="preview-col-wrap">
            <h6 class="text-muted mb-2">Existing Chart Variable Mapping</h6>
            <div class="mapping-table-wrap">
              <!-- Default axis-based mapping -->
              <b-table-simple
                v-if="vizFunction !== 'tableBarChart'"
                bordered
                small
                responsive
                class="mapping-table"
              >
                <b-thead head-variant="light">
                  <b-tr>
                    <b-th>Required</b-th>
                    <b-th>Column</b-th>
                  </b-tr>
                </b-thead>
                <b-tbody>
                  <b-tr
                    v-for="row in existingMappingRows"
                    :key="'existing-' + row.axisLabel"
                  >
                    <b-td>{{ row.axisLabel }}</b-td>
                    <b-td
                      ><code>{{ row.expression }}</code></b-td
                    >
                  </b-tr>
                </b-tbody>
              </b-table-simple>

              <!-- TableBar-specific header/graph mapping -->
              <b-table-simple
                v-else
                bordered
                small
                responsive
                class="mapping-table"
              >
                <b-thead head-variant="light">
                  <b-tr>
                    <b-th>Required</b-th>
                    <b-th>Column</b-th>
                    <b-th>Type</b-th>
                    <b-th v-if="tableBarHasGraph">Graph</b-th>
                  </b-tr>
                </b-thead>
                <b-tbody>
                  <b-tr
                    v-for="row in tableBarExistingMappingRows"
                    :key="'tblbar-' + row.required"
                  >
                    <b-td>{{ row.required }}</b-td>
                    <b-td>{{ row.column }}</b-td>
                    <b-td>{{ row.type }}</b-td>
                    <b-td v-if="tableBarHasGraph">{{ row.graph }}</b-td>
                  </b-tr>
                </b-tbody>
              </b-table-simple>
            </div>
          </b-col>
          <b-col md="6" class="preview-col-wrap">
            <h6 class="text-muted mb-2">Preview Chart Variable Mapping</h6>
            <div class="mapping-table-wrap">
              <!-- Default axis-based mapping -->
              <b-table-simple
                v-if="vizFunction !== 'tableBarChart'"
                bordered
                small
                responsive
                class="mapping-table"
              >
                <b-thead head-variant="light">
                  <b-tr>
                    <b-th>Required</b-th>
                    <b-th>Column</b-th>
                  </b-tr>
                </b-thead>
                <b-tbody>
                  <b-tr
                    v-for="row in previewMappingRows"
                    :key="'preview-' + row.axisLabel"
                  >
                    <b-td>{{ row.axisLabel }}</b-td>
                    <b-td>
                      <input
                        v-model="row.expression"
                        class="inline-mapping-input"
                        placeholder="Enter column or expression"
                      />
                    </b-td>
                  </b-tr>
                </b-tbody>
              </b-table-simple>

              <!-- TableBar-specific column/graph mapping -->
              <b-table-simple
                v-else
                bordered
                small
                responsive
                class="mapping-table"
              >
                <b-thead head-variant="light">
                  <b-tr>
                    <b-th>Required</b-th>
                    <b-th>Column</b-th>
                    <b-th>Type</b-th>
                    <b-th>Graph</b-th>
                  </b-tr>
                </b-thead>
                <b-tbody>
                  <b-tr
                    v-for="row in previewTableBarRows"
                    :key="'tblbar-prev-' + row.required"
                  >
                    <b-td>{{ row.required }}</b-td>
                    <b-td>
                      <input
                        v-model="row.label"
                        class="inline-mapping-input"
                        placeholder="Header label"
                      />
                    </b-td>
                    <b-td>
                      <b-form-select
                        v-model="row.type"
                        :options="tableBarTypeOptions"
                        size="sm"
                        class="type-select"
                      />
                    </b-td>
                    <b-td>
                      <b-form-select
                        v-model="row.graph"
                         :options="tableBarGraphOptions"
                        size="sm"
                        class="type-select"
                      />
                    </b-td>
                  </b-tr>
                </b-tbody>
              </b-table-simple>
            </div>
          </b-col>
        </b-row>
        <div class="preview-btn-row">
          <b-button
            class="btn-sm"
            variant="info"
            :disabled="!isPreviewEnabled"
            @click="generatePreview"
            >Generate Preview</b-button
          >
        </div>
      </b-card>

      <!-- Layer 1 -->
      <b-card class="mb-4 shadow-sm" style="padding: 10px 24px">
        <b-card-title class="mb-4">Menu Configuration</b-card-title>
        <b-row class="mb-3 border-bottom pb-3">
          <b-col md="6">
            <b-form-group label="Parent Menu Name" label-align="left">
              <b-form-input :value="defaultParentMenuId" disabled />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <ValidationProvider
              name="Parent Menu Name"
              rules="required"
              v-slot="{ errors }"
            >
              <b-form-group
                label="Parent Menu Name"
                label-align="left"
                class="position-relative"
              >
                <div class="d-flex align-items-center">
                  <multiselect
                    v-model="parentMenuId"
                    :options="parentMenuIdOptions"
                    :multiple="false"
                    select-label="Select"
                    deselect-label="Unselect"
                    :limit="1"
                    label="name"
                    track-by="id"
                    :close-on-select="false"
                    :clear-on-select="false"
                    :preserve-search="true"
                    class="flex-grow-1"
                  ></multiselect>

                  <!-- Add icon button with tooltip -->
                  <b-button
                    variant="link"
                    class="ml-2 p-0 text-primary"
                    v-b-tooltip.hover="'Add new Parent Menu Id'"
                    @click="showParentMenuModal = true"
                  >
                    <b-icon-plus-circle font-scale="1.5"></b-icon-plus-circle>
                  </b-button>
                </div>
                <span class="text-danger">{{ errors[0] }}</span>
              </b-form-group>
            </ValidationProvider>

            <!-- Modal for adding new parent menu -->
            <b-modal
              v-model="showParentMenuModal"
              title="Add New Parent Menu"
              @ok="addNewParentMenu"
            >
              <b-form-group label="Parent Menu Name">
                <b-form-input
                  v-model="newParentMenuName"
                  placeholder="Enter parent menu name"
                ></b-form-input>
              </b-form-group>
              <b-form-group label="Parent Menu Id">
                <b-form-input
                  v-model="newParentMenuId"
                  placeholder="Enter parent menu id"
                ></b-form-input>
              </b-form-group>

              <template #modal-footer="{ ok, cancel }">
                <b-button variant="secondary" @click="cancel">Cancel</b-button>
                <b-button variant="primary" @click="ok">OK</b-button>
              </template>
            </b-modal>
          </b-col>
          <!-- <b-col md="6">
            <b-form-group label="Menu Id" label-align="left">
              <b-form-input :value="fetchDefaultvalues[0].menuId" disabled />
            </b-form-group>
          </b-col> -->
          <!-- <b-col md="6">
            <ValidationProvider
              name="Menu Id"
              rules="required"
              v-slot="{ errors }"
            >
              <b-form-group label="Menu Id" label-align="left">
                <b-form-input
                  v-model="menuId"
                  :placeholder="menuIdPlaceholder"
                />
                <span class="text-danger">{{ errors[0] }}</span>
              </b-form-group>
            </ValidationProvider>
          </b-col> -->
          <b-col md="6">
            <b-form-group label="Menu Name" label-align="left">
              <b-form-input :value="defaultMenuName" disabled />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <ValidationProvider
              name="Menu Name"
              rules="required"
              v-slot="{ errors }"
            >
              <b-form-group label="Menu Name" label-align="left">
                <b-form-input
                  v-model="menuName"
                  :placeholder="menuNamePlaceholder"
                />
                <span class="text-danger">{{ errors[0] }}</span>
              </b-form-group>
            </ValidationProvider>
          </b-col>
        </b-row>
        <b-row class="mb-3 border-bottom pb-3">
          <b-col md="6">
            <b-form-group label="Screen Title" label-align="left">
              <b-form-input :value="defaultMenuName" disabled />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <ValidationProvider
              name="Screen Title"
              rules="required"
              v-slot="{ errors }"
            >
              <b-form-group label="Screen Title" label-align="left">
                <b-form-input
                  v-model="screenName"
                  placeholder="Enter the Screen Title"
                />
                <span class="text-danger">{{ errors[0] }}</span>
              </b-form-group>
            </ValidationProvider>
          </b-col>
          <!--        <b-col md="6">-->
          <!--          <b-form-group label="Filter SQL Id" label-align="left">-->
          <!--            <b-form-input :value="fetchDefaultvalues[0].screenletListbox1_sqlId" disabled />-->
          <!--          </b-form-group>-->
          <!--        </b-col>-->
          <!--        <b-col md="6">-->
          <!--          <b-form-group label="Filter SQL Id" label-align="left">-->
          <!--            <b-form-input v-model="filterSqlId" placeholder="Enter the Filter SQL"/>-->
          <!--          </b-form-group>-->
          <!--        </b-col>-->
          <!--        <b-col md="6">-->
          <!--          <b-form-group label="Filter Syspk" label-align="left">-->
          <!--            <b-form-input :value="JSON.stringify(defaultFilterSyspk)" disabled />-->
          <!--          </b-form-group>-->
          <!--        </b-col>-->
          <!--        <b-col md="6">-->
          <!--          <b-form-group label="Filter Syspk" label-align="left">-->
          <!--            <b-form-input v-model="filterSyspk" placeholder="Enter the Filter Syspk"/>-->
          <!--          </b-form-group>-->
          <!--        </b-col>-->
          <!--        <b-col md="6">-->
          <!--          <b-form-group label="Data SQL Id" label-align="left">-->
          <!--            <b-form-input :value="fetchDefaultvalues[0].datasqlId" disabled />-->
          <!--          </b-form-group>-->
          <!--        </b-col>-->
          <!--        <b-col md="6">-->
          <!--          <ValidationProvider name="Data SQL Id" rules="required" v-slot="{ errors }">-->
          <!--            <b-form-group label="Data SQL Id" label-align="left">-->
          <!--              <b-form-input v-model="dataSqlId" placeholder="Enter the Data SQL" />-->
          <!--              <span class="text-danger">{{ errors[0] }}</span>-->
          <!--            </b-form-group>-->
          <!--          </ValidationProvider>-->
          <!--        </b-col>-->
          <!-- <b-col md="6">
            <b-form-group label="Viz Type" label-align="left">
              <b-form-input
                :value="fetchDefaultvalues[0].viztypeName"
                disabled
              />
            </b-form-group>
          </b-col> -->
          <!-- <b-col md="6">
            <ValidationProvider
              name="Viz Type"
              rules="required"
              v-slot="{ errors }"
            >
              <b-form-group label="Viz Type" label-align="left">
                <multiselect
                  v-model="vizTypeName"
                  :options="vizTypeOptions"
                  :multiple="false"
                  select-label="Select"
                  deselect-label="Unselect"
                  :limit="1"
                  :close-on-select="false"
                  :clear-on-select="false"
                  :preserve-search="true"
                >
                </multiselect>
                <span class="text-danger">{{ errors[0] }}</span>
              </b-form-group>
            </ValidationProvider>
          </b-col> -->
          <!--        <b-col sm="4">-->
          <!--          <b-form-group label="Viz Size ID">-->
          <!--            <b-form-input :value="fetchDefaultvalues[0].vizletSizeId" disabled />-->
          <!--          </b-form-group>-->
          <!--        </b-col>-->
          <!--        <b-col sm="4">-->
          <!--          <b-form-group label="Viz Size ID">-->
          <!--            <b-form-select v-model="vizSizeId" :options="vizSizeOptions" required />-->
          <!--          </b-form-group>-->
          <!--        </b-col>-->
        </b-row>
        <b-row>
          <!-- X-Axis Name -->
          <b-col md="6" v-if="vizFunction !== 'tableBarChart'">
            <b-form-group label="X-Axis Name" label-align="left">
              <b-form-input
                :value="fetchDefaultvalues[0].x1_axisDisplayName"
                disabled
              />
            </b-form-group>
          </b-col>
          <b-col md="6" v-if="vizFunction !== 'tableBarChart'">
            <b-form-group label="X-Axis Name" label-align="left">
              <b-form-input
                v-model="x_axisName"
                placeholder="Enter the XAxis Name"
              />
            </b-form-group>
          </b-col>

          <!-- Y-Axis Name -->
          <b-col md="6" v-if="vizFunction !== 'tableBarChart'">
            <b-form-group label="Y-Axis Name" label-align="left">
              <b-form-input
                :value="fetchDefaultvalues[0].y1_axisDisplayName"
                disabled
              />
            </b-form-group>
          </b-col>
          <b-col md="6" v-if="vizFunction !== 'tableBarChart'">
            <b-form-group label="Y-Axis Name" label-align="left">
              <b-form-input
                v-model="y_axisName"
                placeholder="Enter the YAxis Name"
              />
            </b-form-group>
          </b-col>

          <!-- Viz Title Name -->
          <!-- <b-col md="6">
            <b-form-group label="Viz Title" label-align="left">
              <b-form-input
                :value="fetchDefaultvalues[0].vizletTitleDpName"
                disabled
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <ValidationProvider
              name="Viz Title"
              rules="required"
              v-slot="{ errors }"
            >
              <b-form-group label="Viz Title" label-align="left">
                <b-form-input
                  v-model="vizTitleName"
                  required
                  placeholder="Enter the Viz Title"
                />
                <span class="text-danger">{{ errors[0] }}</span>
              </b-form-group>
            </ValidationProvider>
          </b-col> -->

          <!-- X-Axis Tooltip -->
          <b-col md="6" v-if="vizFunction !== 'tableBarChart'">
            <b-form-group label="X-Axis Tooltip" label-align="left">
              <b-form-input
                :value="fetchDefaultvalues[0].x1_metricTooltip"
                disabled
              />
            </b-form-group>
          </b-col>
          <b-col md="6" v-if="vizFunction !== 'tableBarChart'">
            <b-form-group label="X-Axis Tooltip" label-align="left">
              <b-form-input
                v-model="x_axisTooltip"
                placeholder="Enter the XAxis Tooltip"
              />
            </b-form-group>
          </b-col>

          <!-- Y-Axis Tooltip -->
          <b-col md="6" v-if="vizFunction !== 'tableBarChart'">
            <b-form-group label="Y-Axis Tooltip" label-align="left">
              <b-form-input
                :value="fetchDefaultvalues[0].y1_metricTooltip"
                disabled
              />
            </b-form-group>
          </b-col>
          <b-col md="6" v-if="vizFunction !== 'tableBarChart'">
            <b-form-group label="Y-Axis Tooltip" label-align="left">
              <b-form-input
                v-model="y_axisTooltip"
                placeholder="Enter the YAxis Tooltip"
              />
            </b-form-group>
          </b-col>

          <!-- X-Axis Data Name -->
          <!-- <b-col md="6" v-if="vizFunction !== 'tableBarChart'">
            <b-form-group label="X-Axis Data Name" label-align="left">
              <b-form-input
                :value="fetchDefaultvalues[0].x1_dataFieldName"
                disabled
              />
            </b-form-group>
          </b-col> -->
          <!-- <b-col md="6" v-if="vizFunction !== 'tableBarChart'">
            <b-form-group label="X-Axis Data Name" label-align="left">
              <b-form-input
                v-model="x_axisDataName"
                placeholder="Enter the XAxis Data Name"
              />
            </b-form-group>
          </b-col> -->

          <!-- Y-Axis Data Name -->
          <!-- <b-col md="6" v-if="vizFunction !== 'tableBarChart'">
            <b-form-group label="Y-Axis Data Name" label-align="left">
              <b-form-input
                :value="fetchDefaultvalues[0].y1_dataFieldName"
                disabled
              />
            </b-form-group>
          </b-col> -->
          <!-- <b-col md="6" v-if="vizFunction !== 'tableBarChart'">
            <b-form-group label="Y-Axis Data Name" label-align="left">
              <b-form-input
                v-model="y_axisDataName"
                placeholder="Enter the YAxis Data Name"
              />
            </b-form-group>
          </b-col> -->
          <!-- <b-col md="6" v-if="vizFunction === 'tableBarChart'">
            <b-form-group label="Table Headers" label-align="left">
              <b-form-textarea
                :value="
                  JSON.stringify(fetchDefaultvalues[0].reportHeaderColumnDp)
                "
                disabled
              />
            </b-form-group>
          </b-col> -->
        </b-row>
        <div class="d-flex justify-content-end mt-4">
          <b-button variant="danger" @click="resetForm" class="mr-2">
            Reset
          </b-button>
          <b-button variant="primary" @click="submitForm">
            Submit
          </b-button>
        </div>
      </b-card>
    </div>
  </ValidationObserver>
</template>

<script>
import axios from "axios";
import { ValidationProvider, ValidationObserver, extend } from "vee-validate";
import { required } from "vee-validate/dist/rules";
import Multiselect from "vue-multiselect/src/Multiselect.vue";
import { BIconPlusCircle } from "bootstrap-vue";
import { apiAbort } from "@/common/apiAbort";
import { FwVizChartInfo } from "@/models";
import { Parser as SqlParser } from "node-sql-parser";

const sqlParser = new SqlParser();

// Add required rule
extend("required", {
  ...required,
  message: "{_field_} is required",
});

export default {
  name: "menuConfig",
  props: {
    embedded: {
      type: Boolean,
      default: false,
    },
    selectedOptions: {
      type: Object,
      default: () => ({}),
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
  },
  components: {
    Multiselect,
    ValidationProvider,
    ValidationObserver,
    BIconPlusCircle,
  },
  watch: {
    filters: {
      handler() {
        // Only re-init and re-render when filters prop changes
        this.initMenuDefaults(this.activeMenuId);
        this.renderExistingChart();
      },
      deep: true,
    },
    vizParams: {
      handler() {
        this.initPreviewMapping();
      },
      deep: true,
      immediate: true,
    },
    dataSqlId: {
      handler(sql) {
        if (this.vizFunction !== "tableBarChart") return;
        this.syncTableBarPreviewRowsFromSql(sql);
      },
    },
  },
  created() {
    console.log("selectedOptions", this.selectedOptions);
    this.initMenuDefaults(this.activeMenuId);
  },
  data: () => ({
    id: `VIZ-${new Date().getTime()}`,
    // Layer 1
    loadMenu: true,
    defaultParentMenuId: null,
    parentMenuId: null,
    parentMenuIdOptions: [],
    radioValue: "1",
    dimension: "2D",
    dimensionOptions: [
      { text: "2D", value: "2D" },
      { text: "3D", value: "3D" },
    ],

    // Layer 2
    menuId: "",
    menuIdPlaceholder: "Enter Menu Id",
    defaultMenuName: "",
    menuName: "",
    menuNamePlaceholder: "Enter Menu Name",

    // Layer 3
    screenName: "",
    screenNamePlaceholder: "Enter Screen Name",
    filterSqlId: "",
    filterSqlIdPlaceholder: "Enter Filter SQL Id",
    defaultFilterSyspk: "",
    filterSyspk: "",
    filterSyspkPlaceholder: "Enter Filter Syspk",

    // Layer 4
    dataSqlId: "",
    dataSqlIdPlaceholder: "Enter Data SQL Id",
    vizTypeName: "",
    vizTypeOptions: [
      /* viz type options */
    ],
    vizSizeId: "VS72",
    vizSizeOptions: [
      /* viz size options */
    ],

    // Layer 5
    x_axisName: "",
    xAxisNamePlaceholder: "Enter X-Axis Name",
    y_axisName: "",
    yAxisNamePlaceholder: "Enter Y-Axis Name",
    vizTitleNamePlaceholder: "Enter Viz Title Name",
    vizTitleName: "",
    x_axisTooltip: "",
    xAxisTooltipPlaceholder: "Enter X-Axis Tooltip",
    y_axisTooltip: "",
    yAxisTooltipPlaceholder: "Enter Y-Axis Tooltip",
    x_axisDataName: "",
    xAxisDataNamePlaceholder: "Enter X-Axis Data Name",
    y_axisDataName: "",
    yAxisDataNamePlaceholder: "Enter Y-Axis Data Name",
    tableHeaders: [],
    nextFieldId: 0,
    showParentMenuModal: false,
    newParentMenuName: "",
    newParentMenuId: "",
    previewLoading: false,
    previewError: "",
    previewData: [],
    previewConfig: {},
    selectedColumns: {},
    selectedTable: "",
    vizFunction: "",
    previewMappingRows: [],
    previewTableBarRows: [],
    tableBarTypeOptions: [
      { value: "Dimension", text: "Dimension" },
      { value: "Metric", text: "Metric" },
    ],
    tableBarGraphOptions: [
      { value: "no", text: "No" },
      { value: "yes", text: "Yes" },
    ],
    defaultChartData: {
      datasql:"",
      width: 800,
      height: 300,
      axisFontSize: 8,
      axisFontColor: "black",
      axisLabelFontSize: 6,
      axisLabelFontColor: "black",
      x1AxisDisplayName: "",
      x2AxisDisplayName: "",
      y1AxisDisplayName: "",
      y2AxisDisplayName: "",
      x1DataFieldName: "",
      x2DataFieldName: "",
      y1DataFieldName: "",
      y2DataFieldName: "",
      reportHeaderColumnDp: "",
      x1D3ScaleType: "timeScale",
      x2D3ScaleType: "timeScale",
      y1D3ScaleType: "linearScale",
      y2D3ScaleType: "linearScale",
      reportHeaderColumnDpWordwrap: {
        wordwrap: { "table heading": "0", "table data": "0" },
      },
      tabReportJson: {},
      x1AxisLabelCharLimitsJson: {
        break: { value1: "0", value2: "/( |,|_|(?=[A-Z]))/" },
      },
      margin: {
        top: 10,
        right: 50,
        bottom: 50,
        left: 50,
      },
    },
  }),
  computed: {
    activeMenuId() {
      // When filters have real values, drive menuId from selectedOptions.menu_id
      const hasFilters = this.filters && Object.keys(this.filters).length > 0;

      if (hasFilters) {
        const sel = this.selectedOptions || {};
        const ids = sel.menu_id;
        if (Array.isArray(ids) && ids.length && ids[0]) {
          return ids[0];
        }
      }

      // Fallback default when no filters / menu selection available yet
      return "I11YTD";
    },
    vizParams() {
      const vizData = this.$store.state.fwConfigScreenElement.filter(
        (item) => item.menuId === this.activeMenuId,
      );
      if (!vizData.length) return {};
      const allVizType = this.$store.state.vizType;
      const matched = allVizType.find(
        (t) => t.viztypeId === vizData[0].vizletId,
      );
      return matched?.vizParams || {};
    },
    fetchDefaultvalues() {
      const vizData = this.$store.state.fwConfigScreenElement.filter(
        (item) => item.menuId === this.activeMenuId,
      );
      console.log(vizData);
      return vizData;
    },
    cleanedDataSql() {
      let rawSql = this.fetchDefaultvalues?.[0]?.datasql || "";
      if (!rawSql) return "";

      // Step 1: Remove fw placeholders
      rawSql = rawSql
        .replace(/\bfw_where_clause\b/gi, "")
        .replace(/\bfw_and_clause\b/gi, "")
        .replace(/\bfw_core_config_conn\b/gi, "");

      // Step 2: Extract SELECT part
      const selectMatch = rawSql.match(/select([\s\S]*?)from/i);
      if (!selectMatch) return rawSql.trim();

      const selectPart = selectMatch[1];
      const afterFrom = rawSql.substring(
        selectMatch.index + selectMatch[0].length - 4,
      );

      const columns = selectPart
        .replace(/\n/g, " ")
        .match(/(?:[^,(]|\([^)]*\))+/g);

      const aliasMap = {};
      const newColumns = [];

      columns.forEach((col) => {
        let column = col.trim();

        const aliasMatch = column.match(/\bas\s+([a-zA-Z0-9_]+)/i);

        if (aliasMatch) {
          const alias = aliasMatch[1];
          const original = column.replace(/\bas\s+[a-zA-Z0-9_]+/i, "").trim();

          aliasMap[alias] = original;
          newColumns.push(original);
        } else {
          newColumns.push(column);
        }
      });

      // Step 3: Replace alias usage in rest of query
      let updatedAfterFrom = afterFrom;

      Object.keys(aliasMap).forEach((alias) => {
        const regex = new RegExp(`\\b${alias}\\b`, "gi");
        updatedAfterFrom = updatedAfterFrom.replace(regex, aliasMap[alias]);
      });

      const finalSql = `select ${newColumns.join(", ")} ${updatedAfterFrom}`;

      return finalSql.replace(/\n\s*\n/g, "\n").trim();
    },
    parsedExistingSqlAliases() {
      const rawSql = this.fetchDefaultvalues?.[0]?.datasql || "";
      if (!rawSql) return {};

      const aliasMap = {};

      try {
        let ast = sqlParser.astify(rawSql, { database: "PostgreSQL" });
        if (Array.isArray(ast)) ast = ast[0];
        if (!ast || ast.type !== "select" || !Array.isArray(ast.columns))
          return {};

        ast.columns.forEach((col) => {
          if (!col.as) return;
          // Build a minimal SELECT using only the expression (no alias)
          const simpleSelectAst = {
            with: null,
            type: "select",
            options: null,
            distinct: null,
            columns: [
              {
                expr: col.expr,
                as: null,
              },
            ],
            into: { position: null },
            from: null,
            where: null,
            groupby: null,
            having: null,
            orderby: null,
            limit: null,
            locking_read: null,
            window: null,
            collate: null,
          };

          let exprSql = "";
          try {
            const sqlCol = sqlParser.sqlify(simpleSelectAst, {
              database: "PostgreSQL",
            });
            exprSql = sqlCol.replace(/^select\s+/i, "").trim();
          } catch (e) {
            exprSql = "";
          }

          if (exprSql) {
            aliasMap[col.as] = exprSql;
          }
        });
      } catch (e) {
        // On parse error, fall back to empty alias map
        return {};
      }

      return aliasMap;
    },
    isPreviewEnabled() {
      if (!this.dataSqlId || !this.dataSqlId.trim()) return false;
      if (this.vizFunction === "tableBarChart") {
        return this.previewTableBarRows.length > 0;
      }
      if (!this.previewMappingRows.length) return false;
      return this.previewMappingRows.every(
        (row) => row.expression && row.expression.trim(),
      );
    },
    existingMappingRows() {
      if (!this.vizParams || !Object.keys(this.vizParams).length) return [];

      const aliasMap = this.parsedExistingSqlAliases;

      return Object.entries(this.vizParams).map(([label, alias]) => ({
        axisLabel: label,
        alias,
        expression: aliasMap[alias] || "—",
        type:
          typeof alias === "string" && alias.toLowerCase().startsWith("metric")
            ? "Metric"
            : "Dimension",
      }));
    },
    tableBarHasGraph() {
      if (this.vizFunction !== "tableBarChart") return false;
      const headerMap =
        this.fetchDefaultvalues?.[0]?.reportHeaderColumnDp || {};
      return Object.values(headerMap).some(
        (v) => typeof v === "string" && v.toLowerCase().startsWith("graph"),
      );
    },
    tableBarExistingMappingRows() {
      if (this.vizFunction !== "tableBarChart") return [];
      const headerMap =
        this.fetchDefaultvalues?.[0]?.reportHeaderColumnDp || {};

      const entries = Object.entries(headerMap).filter(
        ([, value]) =>
          !(
            typeof value === "string" && value.toLowerCase().startsWith("graph")
          ),
      );

      const graphLabelSet = new Set();
      Object.entries(headerMap).forEach(([label, value]) => {
        if (
          typeof value === "string" &&
          value.toLowerCase().startsWith("graph")
        ) {
          const baseLabel = label.replace(/#+.*$/, "").replace(/#$/, "");
          graphLabelSet.add(baseLabel);
        }
      });

      let index = 1;
      return entries.map(([label, value]) => {
        const isMetric =
          typeof value === "string" && value.toLowerCase().startsWith("metric");
        const hasGraph = isMetric && graphLabelSet.has(label);
        const required = `Col${index++}`;
        return {
          required,
          column: label,
          field: value,
          type: isMetric ? "Metric" : "Dimension",
          graph: hasGraph ? "yes" : "no",
        };
      });
    },
  },
  mounted() {
    this.renderExistingChart();
  },
  methods: {
    initMenuDefaults(menuId) {
      const uniqueParentMenuIdsMap = new Map();
      this.$store.state.menuMasterData.forEach((item) => {
        if (item.menuId !== null && item.menuId !== undefined) {
          uniqueParentMenuIdsMap.set(item.menuId, {
            id: item.menuId,
            name: item.menuName,
          });
        }
      });
      const allParentMenuIds = Array.from(uniqueParentMenuIdsMap.values());
      this.parentMenuIdOptions = allParentMenuIds;
      const parentId = this.$store.state.menuMasterFlat.find(
        (item) => item.menuId === menuId,
      ).parentMenuId;
      this.defaultParentMenuId = this.$store.state.menuMasterData.find(
        (item) => item.menuId === parentId,
      ).menuName;
      console.log("default", this.$store.state.menuMasterData);
      this.defaultMenuName = this.$store.state.menuMasterFlat.find(
        (item) => item.menuId === menuId,
      ).menuName;
      this.vizTypeOptions = this.$store.state.vizType
        .map((item) => item.viztypeName)
        .sort();
      this.defaultFilterSyspk = this.$store.state.fwConfigMenuScreenRender.find(
        (item) => item.menuId === menuId,
      ).screenletListboxSyspkArray;
      console.log(this.defaultFilterSyspk);
    },
    splitSelectColumns(selectPart) {
      const columns = [];
      let depth = 0;
      let current = "";

      for (const char of selectPart) {
        if (char === "(") depth++;
        if (char === ")") depth--;
        if (char === "," && depth === 0) {
          columns.push(current.trim());
          current = "";
        } else {
          current += char;
        }
      }
      if (current.trim()) columns.push(current.trim());

      return columns;
    },
    initPreviewMapping() {
      if (!this.vizParams || !Object.keys(this.vizParams).length) return;
      this.previewMappingRows = Object.entries(this.vizParams).map(
        ([label, alias]) => ({
          axisLabel: label,
          alias,
          expression: "",
        }),
      );
    },
    renderExistingChart() {
      let vizData = this.$store.state.fwConfigScreenElement.filter(
        (item) => item.menuId === this.activeMenuId,
      );
      if (!vizData || vizData.length === 0) {
        if (this.$refs.r1c1) this.$refs.r1c1.innerHTML = "";
        return;
      }

      // Clear existing chart so we don't see the old one while new data loads
      if (this.$refs.r1c1) this.$refs.r1c1.innerHTML = "";

      let allVizType = this.$store.state.vizType;
      let filter = allVizType.filter(
        (t) => t.viztypeId === vizData[0].vizletId,
      )[0];
      let vizFunction = filter.viztypeFunctionName;
      this.vizFunction = vizFunction;

      const v = vizData[0];
      this.defaultChartData.x1D3ScaleType = v.x1_d3scaleType || "bandScale";
      this.defaultChartData.x2D3ScaleType = v.x2_d3scaleType || "bandScale";
      this.defaultChartData.y1D3ScaleType = v.y1_d3scaleType || "linearScale";
      this.defaultChartData.y2D3ScaleType = v.y2_d3scaleType || "linearScale";

      let margin = {
        top: 50,
        right: 150,
        bottom: 100,
        left: 80,
      };

      let vizunit = vizData[0].vizletSequence;

      let getChartInfo = (vizunit) => {
        let f = vizData[0];
        f.chartWidth = 800;
        f.chartHeight = 300;
        f.axisFontSize = 14;
        f.axisLabelFontSize = 12;
        if (f.length === 0)
          return new FwVizChartInfo(vizunit, margin, {}, false, []);
        return new FwVizChartInfo(
          vizunit,
          margin,
          f,
          vizData[0].visibleFlag,
          f,
        );
      };

      var divId = this.$refs.r1c1;

      let chartData = getChartInfo(vizunit);
      console.log("chartData", chartData, this.vizFunction);

      var url =
        "/api/fetchDataWithParam/" +
        chartData.data.datasqlId +
        "/" +
        "CommonVizClass";
      const controller = apiAbort.new();

      axios
        .post(
          url,
          {},
          {
            signal: controller.signal,
            headers: {
              "info-params": JSON.stringify({
                menuId: this.$route.params.menuId,
                submenuId: this.$route.params.submenuId,
              }),
              "Content-Type": "application/json",
            },
          },
        )
        .then((resp) => {
          this.$commonFn.renderChartCompare(
            "CommonVizClass", // existing viz
            chartData,
            divId,
            this.vizFunction,
            resp.data,
            {},
          );
        });
    },
    buildTableSQL() {
      const columns = this.selectedColumns.join(", ");
      return `
        SELECT ${columns}
        FROM ${this.selectedTable} LIMIT 100
      `;
    },

    syncTableBarPreviewRowsFromSql(sql) {
      if (!sql || !sql.trim()) {
        this.previewTableBarRows = [];
        return;
      }

      let ast;
      try {
        ast = sqlParser.astify(sql, { database: "PostgreSQL" });
      } catch (e) {
        this.previewTableBarRows = [];
        return;
      }
      if (Array.isArray(ast)) ast = ast[0];
      if (!ast || ast.type !== "select" || !Array.isArray(ast.columns)) {
        this.previewTableBarRows = [];
        return;
      }

      const existing = this.previewTableBarRows || [];
      const newRows = ast.columns.map((col, idx) => {
        const prev = existing[idx] || {};
        // Build minimal SELECT to stringify the column expression
        const simpleSelectAst = {
          with: null,
          type: "select",
          options: null,
          distinct: null,
          columns: [col],
          into: { position: null },
          from: null,
          where: null,
          groupby: null,
          having: null,
          orderby: null,
          limit: null,
          locking_read: null,
          window: null,
          collate: null,
        };

        let expression = "";
        try {
          const sqlCol = sqlParser.sqlify(simpleSelectAst);
          expression = sqlCol.replace(/^select\s+/i, "").trim();
        } catch (e) {
          expression = "";
        }

        const defaultLabel =
          prev.label != null ? prev.label : col.as || expression;

        const prevGraph =
          typeof prev.graph === "string" ? prev.graph.toLowerCase() : "";
        const graphDefault = prevGraph.startsWith("y") ? "yes" : "no";

        return {
          required: `Col${idx + 1}`,
          expr: expression,
          label: defaultLabel,
          type: prev.type === "Metric" ? "Metric" : "Dimension",
          graph: graphDefault,
        };
      });
      this.previewTableBarRows = newRows;
    },

    buildTableBarPreview(sql) {
      if (!sql) return { newSql: "", headerMap: {} };

      let ast;
      try {
        ast = sqlParser.astify(sql, { database: "PostgreSQL" });
      } catch (e) {
        return { newSql: sql, headerMap: {} };
      }
      if (Array.isArray(ast)) ast = ast[0];
      if (!ast || ast.type !== "select" || !Array.isArray(ast.columns)) {
        return { newSql: sql, headerMap: {} };
      }

      const rows = this.previewTableBarRows || [];
      let dimIndex = 1;
      let metricIndex = 1;
      const headerEntries = [];
      const newColumns = [];

      rows.forEach((row, idx) => {
        const colAst = ast.columns[idx] || null;

        // Prefer original expression AST when available, fall back to a simple column_ref
        const exprAst =
          colAst && colAst.expr
            ? colAst.expr
            : {
                type: "column_ref",
                table: null,
                column: row.expr,
                collate: null,
              };

        const label = row.label || row.expr;
        const isMetric =
          typeof row.type === "string" &&
          row.type.trim().toLowerCase() === "metric";
        const wantsGraph =
          isMetric &&
          typeof row.graph === "string" &&
          row.graph.trim().length > 0 &&
          row.graph.toLowerCase().startsWith("yes");

        let alias;
        let graphAlias = null;
        if (isMetric) {
          alias = `metric${metricIndex++}`;
          graphAlias = wantsGraph ? `graph${metricIndex - 1}` : null;
        } else {
          alias = `dim${dimIndex++}_name`;
        }

        newColumns.push({
          expr: exprAst,
          as: alias,
        });

        headerEntries.push({
          key: label,
          value: alias,
          graphKey: wantsGraph ? `${label}#` : null,
          graphValue: graphAlias,
        });
      });

      // Replace columns on the parsed AST and regenerate SQL (PostgreSQL, no backticks)
      ast.columns = newColumns;
      let newSql;
      try {
        newSql = sqlParser.sqlify(ast, { database: "PostgreSQL" });
      } catch (e) {
        newSql = sql;
      }

      // Build headerMap with all non-graph entries first, then all graph entries
      const headerMap = {};
      headerEntries.forEach((e) => {
        headerMap[e.key] = e.value;
      });
      headerEntries.forEach((e) => {
        if (e.graphKey && e.graphValue) {
          headerMap[e.graphKey] = e.graphValue;
        }
      });

      return { newSql, headerMap };
    },

    standardizeSQL(sql) {
      if (!sql) return { newSql: "", headerMap: {} };

      const selectMatch = sql.match(/select([\s\S]*?)from/i);
      if (!selectMatch) return { newSql: sql, headerMap: {} };

      const selectPart = selectMatch[1];
      const fromPart = sql.substring(
        selectMatch.index + selectMatch[0].length - 4,
      );

      // Remove line breaks
      const cleanedSelect = selectPart.replace(/\n/g, " ");

      // Split columns safely
      const columns = cleanedSelect.match(/(?:[^,(]|\([^)]*\))+/g);

      let dimIndex = 1;
      let metricIndex = 1;

      const headerMap = {};
      const newColumns = [];

      columns.forEach((col) => {
        let column = col.trim();

        const isAggregate = /(sum|avg|count|min|max|round)/i.test(column);

        // Remove existing alias if present
        column = column.replace(/\bas\s+[a-zA-Z0-9_]+/i, "").trim();

        if (!isAggregate) {
          const alias = `dim${dimIndex}_name`;
          newColumns.push(`${column} as ${alias}`);
          headerMap[column] = alias;
          dimIndex++;
        } else {
          const alias = `metric${metricIndex}`;
          newColumns.push(`${column} as ${alias}`);
          headerMap[column] = alias;
          metricIndex++;
        }
      });

      const newSelectClause = `select ${newColumns.join(", ")} `;
      const newSql = newSelectClause + fromPart;

      return { newSql, headerMap };
    },

    appendDynamicClause(sql) {
      if (!sql) return "";

      const lowerSql = sql.toLowerCase();

      // Avoid duplicate injection
      if (
        lowerSql.includes("fw_where_clause") ||
        lowerSql.includes("fw_and_clause")
      ) {
        return sql;
      }

      const hasWhere = /\bwhere\b/i.test(sql);
      const clause = hasWhere ? "fw_and_clause" : "fw_where_clause";

      // Find the first top-level GROUP BY / ORDER BY / HAVING / LIMIT,
      // ignoring those that appear inside parentheses (e.g. window functions).
      let depth = 0;
      let splitIndex = -1;
      for (let i = 0; i < lowerSql.length; i++) {
        const ch = lowerSql[i];
        if (ch === "(") depth++;
        else if (ch === ")" && depth > 0) depth--;

        if (depth === 0) {
          if (
            lowerSql.startsWith("group by", i) ||
            lowerSql.startsWith("order by", i) ||
            lowerSql.startsWith("having", i) ||
            lowerSql.startsWith("limit", i)
          ) {
            splitIndex = i;
            break;
          }
        }
      }

      if (splitIndex !== -1) {
        // Inject clause before the top-level GROUP/ORDER/HAVING/LIMIT
        return `${sql.slice(0, splitIndex)}\n${clause}\n${sql.slice(
          splitIndex,
        )}`;
      }

      // If no grouping/ordering keywords, append safely at end
      return `${sql}\n${clause}`;
    },

    validateSQL(sql) {
      if (!sql || !sql.trim()) {
        this.showToast("SQL query cannot be empty", "danger");
        return false;
      }

      // Normalize: trim and remove trailing semicolons (allow a single statement ending with ;)
      const trimmed = sql.trim();
      const sqlNoTrailingSemi = trimmed.replace(/;+\s*$/g, "");

      const forbiddenKeywords = [
        "update",
        "delete",
        "truncate",
        "drop",
        "alter",
        "insert",
        "create",
        "grant",
        "revoke",
      ];

      const lowerSql = sqlNoTrailingSemi.toLowerCase();

      // ❌ Block multiple statements (very important)
      if (lowerSql.includes(";")) {
        this.showToast("Multiple SQL statements are not allowed", "danger");
        return false;
      }

      // ❌ Block dangerous keywords
      for (let keyword of forbiddenKeywords) {
        const regex = new RegExp(`\\b${keyword}\\b`, "i");
        if (regex.test(sql)) {
          this.showToast(
            `"${keyword.toUpperCase()}" operations are not allowed`,
            "danger",
          );
          return false;
        }
      }

      // ❌ Must start with SELECT
      if (!/^\s*(select|with)\b/i.test(sqlNoTrailingSemi)) {
        this.showToast("Only SELECT queries are allowed", "danger");
        return false;
      }

      return true;
    },

    showToast(message, variant = "danger") {
      this.$bvToast.toast(message, {
        title: "SQL Validation Error",
        variant: variant,
        solid: true,
        autoHideDelay: 4000,
      });
    },

    buildPreviewSQL(sql) {
      if (!sql) return { newSql: "", headerMap: {} };

      const selectMatch = sql.match(/select([\s\S]*?)from/i);
      if (!selectMatch) return { newSql: sql, headerMap: {} };

      const selectPart = selectMatch[1].replace(/\n/g, " ");
      const fromPart = sql.substring(
        selectMatch.index + selectMatch[0].length - 4,
      );
      const columns = this.splitSelectColumns(selectPart);

      const expressionToAlias = {};
      this.previewMappingRows.forEach((row) => {
        if (row.expression && row.expression.trim()) {
          expressionToAlias[row.expression.trim().toLowerCase()] = row.alias;
        }
      });

      const headerMap = {};
      const newColumns = columns.map((col) => {
        const column = col
          .trim()
          .replace(/\bas\s+[a-zA-Z0-9_]+\s*$/i, "")
          .trim();
        const colLower = column.toLowerCase();

        if (expressionToAlias[colLower]) {
          const alias = expressionToAlias[colLower];
          headerMap[column] = alias;
          return `${column} as ${alias}`;
        }
        return column;
      });

      const newSql = `select ${newColumns.join(", ")} ${fromPart}`;
      return { newSql, headerMap };
    },

    async generatePreview() {
      try {
        this.previewLoading = true;
        this.previewError = "";

        let sqlQuery = "";

        if (this.dataSqlId) {
          sqlQuery = this.dataSqlId;
        }

        if (!this.validateSQL(sqlQuery)) {
          return;
        }

        let result;
        if (this.vizFunction === "tableBarChart") {
          result = this.buildTableBarPreview(sqlQuery);
        } else {
          result = this.buildPreviewSQL(sqlQuery);
        }
        sqlQuery = result.newSql;
        this.defaultChartData.reportHeaderColumnDp = result.headerMap;

        sqlQuery = this.appendDynamicClause(sqlQuery);
        this.defaultChartData.datasql = sqlQuery;

        if (this.selectedTable && this.selectedColumns.length) {
          sqlQuery = this.buildTableSQL();
        }

        const controller = apiAbort.new();
        var divId = this.$refs.r1c2;

        const data = {
          query: sqlQuery,
        };

        console.log("query", sqlQuery, this.defaultChartData, result);

        axios
          .post("/api/fetchData", data, {
            signal: controller.signal,
          })
          .then((resp) => {
            this.$commonFn.renderChartCompare(
              "CommonVizClass",
              this.defaultChartData,
              divId,
              this.vizFunction,
              resp.data,
              {},
            );
          });
      } catch (e) {
        this.previewError = "Failed to generate preview";
      } finally {
        this.previewLoading = false;
      }
    },
    async addNewParentMenu() {
      try {
        // Call your API to add new parent menu
      } catch (error) {
        console.error("Error adding parent menu:", error);
        // Handle error (show toast/message)
      }
    },
    submitForm() {
      this.$refs.observer.validate().then((valid) => {
        if (valid) {
          this.loadMenu = false;
          this.postConfigMenu();
        } else {
          this.$bvToast.toast("Please fill all required fields", {
            title: "Validation Error",
            variant: "danger",
            solid: true,
          });
        }
      });
    },
    postConfigMenu() {
      let jsonString;
      if (this.vizFunction === "tableBarChart") {
        // Pass the headerMap we use for TableBar (reportHeaderColumnDp)
        const headerMap =
          this.defaultChartData.reportHeaderColumnDp ||
          this.fetchDefaultvalues?.[0]?.reportHeaderColumnDp ||
          {};
        jsonString = JSON.stringify(headerMap);
        console.log("postConfigMenu tableHeaders (headerMap)", headerMap);
      } else {
        const transformedHeaders = this.tableHeaders.reduce((acc, current) => {
          acc[current.header] = current.value;
          return acc;
        }, {});
        jsonString = JSON.stringify(transformedHeaders);
        console.log("postConfigMenu tableHeaders", this.tableHeaders);
      }
      const existing = this.fetchDefaultvalues?.[0];
      const formData = {
        parentMenuId: this.parentMenuId.id,
        menuName: this.menuName,
        screenName: this.screenName,
        filterSqlId: this.filterSqlId,
        filterSyspk: this.filterSyspk,
        dataSql: this.defaultChartData.datasql,
        vizTypeName: existing?.viztypeName ?? this.vizTypeName,
        vizletId: existing?.vizletId ?? this.vizletId,
        x_axisName: this.x_axisName,
        y_axisName: this.y_axisName,
        vizTitleName: this.vizTitleName,
        x_axisTooltip: this.x_axisTooltip,
        y_axisTooltip: this.y_axisTooltip,
        x_axisDataName: this.x_axisDataName,
        y_axisDataName: this.y_axisDataName,
        x1_d3scaleType: existing?.x1_d3scaleType ?? this.x1_d3scaleType,
        tableHeaders: jsonString,
      };
      console.log("form data", formData);
      axios
        .post("/api/sp-config-menu", formData)
        .then((resp) => {
          console.log(resp);
          if (resp.status === 200) {
            this.$bvToast.toast(
              "Menu has been successfully configured and saved.",
              {
                title: "Success",
                variant: "success",
                solid: true,
              },
            );
            this.loadMenu = true;
            this.resetForm();

            // Reload after 5 seconds
            setTimeout(() => {
              window.location.reload();
              // or: this.$router.go(0);
            }, 5000);
          }
        })
        .catch((e) => {
          console.log("error", e);
          this.$bvToast.toast(
            "Please try again. If the problem persists, contact support.",
            {
              title: "Failed to Save Menu",
              variant: "error",
              solid: true,
            },
          );
        });
    },
    resetForm() {
      // Reset all form fields to their initial values
      this.parentMenuId = "";
      this.menuId = "";
      this.menuName = "";
      this.screenName = "";
      this.filterSqlId = "";
      this.filterSyspk = "";
      this.dataSqlId = "";
      this.vizTypeName = "";
      this.x_axisName = "";
      this.y_axisName = "";
      this.vizTitleName = "";
      this.x_axisTooltip = "";
      this.y_axisTooltip = "";
      this.x_axisDataName = "";
      this.y_axisDataName = "";
      this.tableHeaders = []; // Reset to default if you have one
    },
    addField() {
      this.tableHeaders.push({
        id: this.nextFieldId++, // Assign a unique ID for :key
        header: "",
        value: "",
      });
    },
    removeField(index) {
      this.tableHeaders.splice(index, 1);
    },
  },
};
</script>

<style scoped>
/* Standalone menu-config scroll container.
   Disabled when used with :embedded=\"true\" so vizlet can control scrolling. */
.menu-page-scroll {
  max-height: calc(100vh - 80px);
  overflow: auto;
  padding: 20px;
  width: 100%;
}

.preview-card,
.preview-card .card-body {
  padding: 5px;
}

/* Chart layout */
.preview-charts {
  display: flex;
  gap: 20px;
}

.chart-box {
  flex: 1;
  min-height: 350px;
  min-width: 300px;
  border: 1px solid #e0e0e0;
  padding: 10px;
}

@media (min-width: 769px) and (max-width: 1300px) {
  .chart-box {
    min-height: 100px;
  }
}

.scrollmenu {
  max-height: 300px;
  overflow: auto;
}

.mapping-table code {
  font-size: 0.85em;
  color: #495057;
  background: #f8f9fa;
  padding: 2px 5px;
  border-radius: 3px;
}

/* Preview card: allow cols and content to shrink */
.preview-col-wrap {
  min-width: 0;
  overflow: hidden;
}

.preview-sql-row .sql-form-group {
  margin-bottom: 0.5rem;
}

.preview-sql-row .sql-form-group >>> textarea,
.preview-card .sql-textarea {
  max-height: 120px;
  font-size: 12px;
  padding: 6px 8px;
  width: 100%;
  box-sizing: border-box;
}

.mapping-table-wrap {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  max-width: 100%;
}

.mapping-table-wrap .mapping-table {
  min-width: 200px;
  font-size: 13px;
}

.mapping-table-wrap .mapping-table th,
.mapping-table-wrap .mapping-table td {
  padding: 4px 6px;
  white-space: nowrap;
}

.mapping-table-wrap .mapping-table td:last-child {
  white-space: normal;
  word-break: break-word;
  max-width: 180px;
}

/* Responsive: shrink on smaller screens */
@media (max-width: 1400px) {
  .preview-sql-row .sql-form-group >>> textarea,
  .preview-card .sql-textarea {
    max-height: 90px;
    font-size: 11px;
    padding: 4px 6px;
  }
  .mapping-table-wrap .mapping-table {
    font-size: 12px;
  }
  .mapping-table-wrap .mapping-table th,
  .mapping-table-wrap .mapping-table td {
    padding: 3px 5px;
  }
  .mapping-table-wrap .mapping-table td:last-child {
    max-width: 140px;
  }
}

@media (max-width: 992px) {
  .preview-sql-row .sql-form-group >>> textarea,
  .preview-card .sql-textarea {
    max-height: 70px;
    font-size: 11px;
  }
  .mapping-table-wrap .mapping-table {
    font-size: 11px;
  }
  .mapping-table-wrap .mapping-table th,
  .mapping-table-wrap .mapping-table td {
    padding: 2px 4px;
  }
  .mapping-table-wrap .mapping-table td:last-child {
    max-width: 120px;
  }
}

@media (max-width: 768px) {
  .preview-sql-row .sql-form-group >>> textarea,
  .preview-card .sql-textarea {
    max-height: 60px;
    font-size: 10px;
    padding: 3px 5px;
  }
  .preview-mapping-row .mapping-table-wrap {
    margin-bottom: 0.5rem;
  }
  .mapping-table-wrap .mapping-table td:last-child {
    max-width: 100%;
  }
}

.inline-mapping-input {
  font-family: "Consolas", "Monaco", "Courier New", monospace;
  font-size: 10px;
  color: #495057;
  background: #f8f9fa;
  border: 1px solid transparent;
  border-radius: 3px;
  padding: 1px 5px;
  width: 100%;
  outline: none;
  transition: border-color 0.15s, background 0.15s;
}

.inline-mapping-input:focus {
  background: #fff;
  border-color: #80bdff;
}

.type-select.custom-select {
  font-family: "Consolas", "Monaco", "Courier New", monospace;
  font-size: 10px;
  color: #495057;
  /* keep Bootstrap's default background-image (caret) */
  background-color: #f8f9fa;
  border-radius: 3px;
  padding: 2px 5px;
  width: 100%;
  height: calc(1.5em);
  line-height: 1;
}

.inline-mapping-input::placeholder {
  color: #adb5bd;
  font-style: italic;
}

.preview-btn-row {
  display: flex;
  justify-content: flex-end;
  padding: 0;
  line-height: 1;
  margin-top: -20px;
}

.preview-btn {
  font-size: 14px;
  background: #17a2b8;
  color: #fff;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  transition: background 0.15s;
}

.preview-btn:hover:not(:disabled) {
  background: #138496;
}

.preview-btn:disabled {
  background: #b0b7bc;
  cursor: not-allowed;
  opacity: 0.8;
}
</style>
